/**
 * 🟥 CUADRO NEGRO V7.3 — AutoFixLoopResultModal.
 *
 * Modal que muestra el resultado del loop autónomo (POST /api/github/auto-fix-loop).
 * Por cada iteración:
 *   - número
 *   - fixes aplicados + skipped
 *   - commit (link a GitHub)
 *   - CI status (success/failure/timeout/etc.)
 *   - CI log summary IA (si falló)
 */
import {
  Sparkles, X, CheckCircle2, XCircle, AlertCircle, ExternalLink, GitCommit,
  FileEdit, Workflow, Loader2,
} from 'lucide-react';

const finalStyles = {
  success: { color: 'text-emerald-300', bg: 'from-emerald-900/40 to-emerald-800/20', border: 'border-emerald-500/40', Icon: CheckCircle2, label: 'CI verde · PR listo para merge' },
  failure: { color: 'text-rose-300', bg: 'from-rose-900/40 to-rose-800/20', border: 'border-rose-500/40', Icon: XCircle, label: 'CI sigue fallando tras todos los intentos' },
  timeout: { color: 'text-amber-300', bg: 'from-amber-900/40 to-amber-800/20', border: 'border-amber-500/40', Icon: Loader2, label: 'Timeout esperando CI' },
  no_workflow: { color: 'text-sky-300', bg: 'from-sky-900/40 to-sky-800/20', border: 'border-sky-500/40', Icon: AlertCircle, label: 'No hay workflow CI configurado — fixes aplicados sin verificar' },
  no_fixes: { color: 'text-slate-300', bg: 'from-slate-800 to-slate-900', border: 'border-slate-700', Icon: AlertCircle, label: 'La IA no encontró fixes para aplicar' },
};

const ciStyles = {
  success: { color: 'text-emerald-300', Icon: CheckCircle2, label: 'CI verde' },
  failure: { color: 'text-rose-300', Icon: XCircle, label: 'CI rojo' },
  cancelled: { color: 'text-slate-300', Icon: AlertCircle, label: 'Cancelado' },
  timeout: { color: 'text-amber-300', Icon: Loader2, label: 'Timeout' },
  no_workflow: { color: 'text-sky-300', Icon: AlertCircle, label: 'Sin workflow' },
  no_fixes: { color: 'text-slate-400', Icon: AlertCircle, label: 'Sin fixes' },
};

export default function AutoFixLoopResultModal({ result, isEs = true, onClose }) {
  if (!result) return null;
  const iterations = result.iterations || [];
  const finalMeta = finalStyles[result.final_status] || finalStyles.failure;
  const FinalIcon = finalMeta.Icon;

  return (
    <div
      className="fixed inset-0 z-[8800] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="autofix-loop-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full max-w-3xl max-h-[90vh] bg-gradient-to-b from-slate-900 to-slate-950 border border-indigo-500/30 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        <header className="flex items-center justify-between px-5 py-3 border-b border-slate-800 bg-gradient-to-r from-indigo-900/40 to-violet-900/30">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-300" />
            <div>
              <h2 className="text-base font-bold text-white">{isEs ? 'Auto-Fix Loop IA' : 'AI Auto-Fix Loop'}</h2>
              <p className="text-[11px] text-indigo-300/80">
                {iterations.length} {isEs ? 'iteraciones · ' : 'iterations · '}{result.total_commits || 0} commits
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="autofix-loop-close"
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        {/* Final status banner */}
        <div className={`mx-4 mt-3 p-3 rounded-lg bg-gradient-to-r ${finalMeta.bg} border ${finalMeta.border} flex items-center gap-3`}>
          <FinalIcon className={`w-6 h-6 ${finalMeta.color} ${result.final_status === 'timeout' ? 'animate-spin' : ''}`} />
          <div className="flex-1 min-w-0">
            <div className={`text-sm font-semibold ${finalMeta.color}`}>{finalMeta.label}</div>
            <div className="text-[11px] text-slate-400">
              {isEs ? 'Branch: ' : 'Branch: '}<span className="font-mono">{result.pr_branch}</span>
            </div>
          </div>
          {result.pr_url && (
            <a
              href={result.pr_url}
              target="_blank"
              rel="noreferrer"
              data-testid="autofix-loop-pr-link"
              className="px-2 py-1 text-[11px] rounded-md bg-slate-800 hover:bg-slate-700 text-slate-300 flex items-center gap-1"
            >
              PR <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {iterations.length === 0 ? (
            <div className="text-center text-slate-500 text-sm py-12">
              {isEs ? 'Sin iteraciones realizadas.' : 'No iterations performed.'}
            </div>
          ) : (
            iterations.map((it) => {
              const ciMeta = ciStyles[it.ci_status] || ciStyles.failure;
              const CiIcon = ciMeta.Icon;
              return (
                <div
                  key={it.iteration}
                  className="rounded-lg border border-slate-800 bg-slate-900/60"
                  data-testid={`autofix-loop-iter-${it.iteration}`}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between gap-2 p-3 border-b border-slate-800">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                        {isEs ? `Iteración ${it.iteration}` : `Iteration ${it.iteration}`}
                      </span>
                      <CiIcon className={`w-4 h-4 ${ciMeta.color} ${it.ci_status === 'timeout' ? 'animate-spin' : ''}`} />
                      <span className={`text-xs ${ciMeta.color}`}>{ciMeta.label}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {it.commit_url && (
                        <a
                          href={it.commit_url}
                          target="_blank"
                          rel="noreferrer"
                          className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-slate-800"
                          title={isEs ? 'Ver commit' : 'View commit'}
                          data-testid={`autofix-loop-commit-${it.iteration}`}
                        >
                          <GitCommit className="w-3.5 h-3.5" />
                        </a>
                      )}
                      {it.workflow_run_url && (
                        <a
                          href={it.workflow_run_url}
                          target="_blank"
                          rel="noreferrer"
                          className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-slate-800"
                          title={isEs ? 'Ver CI run' : 'View CI run'}
                          data-testid={`autofix-loop-run-${it.iteration}`}
                        >
                          <Workflow className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Applied fixes */}
                  {it.applied_fixes && it.applied_fixes.length > 0 && (
                    <div className="p-3 space-y-1">
                      <div className="text-[10px] uppercase tracking-wider text-emerald-400">
                        {isEs ? `Aplicados (${it.applied_fixes.length})` : `Applied (${it.applied_fixes.length})`}
                      </div>
                      {it.applied_fixes.slice(0, 8).map((fx, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-[11px]">
                          <FileEdit className="w-3 h-3 text-emerald-400 shrink-0" />
                          <span className="font-mono text-slate-200 truncate flex-1">{fx.path}</span>
                          <span className="text-slate-500">L{fx.line}</span>
                          <span className="text-[10px] text-amber-300 uppercase">{fx.action}</span>
                        </div>
                      ))}
                      {it.applied_fixes.length > 8 && (
                        <div className="text-[10px] text-slate-500 italic">
                          {isEs ? `+${it.applied_fixes.length - 8} más…` : `+${it.applied_fixes.length - 8} more…`}
                        </div>
                      )}
                    </div>
                  )}

                  {/* CI log summary (if failed) */}
                  {it.ci_log_summary && (
                    <details className="px-3 pb-3 group">
                      <summary className="cursor-pointer text-[10px] uppercase tracking-wider text-rose-400 flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> {isEs ? 'Resumen IA del CI fallido' : 'AI summary of CI failure'}
                      </summary>
                      <pre className="mt-1 text-[11px] whitespace-pre-wrap text-slate-200 bg-slate-950 p-2.5 rounded border border-rose-500/20 max-h-48 overflow-y-auto">
                        {it.ci_log_summary}
                      </pre>
                    </details>
                  )}

                  {/* Error */}
                  {it.error && (
                    <div className="px-3 pb-3 text-[11px] text-rose-300 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {it.error}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <footer className="px-5 py-2.5 border-t border-slate-800 bg-slate-900/60 text-center">
          <p className="text-[11px] text-slate-500 italic">
            {isEs
              ? '🤖 DamyxIA — IDE + Git + PRs + Review IA + Auto-Fix IA + CI/CD + Deploys + Loop autónomo.'
              : '🤖 DamyxIA — IDE + Git + PRs + AI Review + Auto-Fix + CI/CD + Deploys + Autonomous loop.'}
          </p>
        </footer>
      </div>
    </div>
  );
}
